import type { DialogRef } from '@angular/cdk/dialog';
import { Dialog } from '@angular/cdk/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import type { Type } from '@angular/core';
import { inject, reflectComponentType, Service } from '@angular/core';
import { isEqual } from 'lodash';
import { filter, fromEvent, map, merge, takeUntil, zip } from 'rxjs';
import { OnyxModalComponent } from '../components/modal/onyx-modal/onyx-modal.component';
import type { OnyxModalConfig, OnyxModalResult } from './interfaces';

@Service()
export class OnyxModalService {
  private readonly dialog = inject(Dialog);

  private readonly ONYX_MODAL_TAG_NAME =
    reflectComponentType(OnyxModalComponent)!.selector.toUpperCase();

  private openedModals: {
    component: Type<any>;
    data?: unknown;
    dialogRef: DialogRef<any>;
  }[] = [];

  public open<Data = unknown, Result = unknown>(
    component: Type<any>,
    data?: Data,
    config?: OnyxModalConfig,
  ): OnyxModalResult<Result> {
    const getModalElement = (dialogRef: DialogRef<any>): Element | null => {
      const dialogElement: HTMLElement =
        dialogRef.componentRef!.location.nativeElement;
      const modalElement = dialogElement.querySelector(
        this.ONYX_MODAL_TAG_NAME,
      );
      return modalElement;
    };

    const forceClose = (modalElement: Element | null) => {
      const forceCloseEvent = new CustomEvent('onyxModalForceClose');
      modalElement?.dispatchEvent(forceCloseEvent);
    };

    const close = (modalElement: Element | null) => {
      const closeEvent = new CustomEvent('onyxModalClose');
      modalElement?.dispatchEvent(closeEvent);
    };

    const openedModal = this.openedModals.find(
      (openedModal) =>
        openedModal.component === component && isEqual(openedModal.data, data),
    );
    if (openedModal) {
      const modalElement = getModalElement(openedModal.dialogRef);
      const wasOnFront = this.openedModals.at(-1) === openedModal;

      if (wasOnFront) {
        close(modalElement);

        return {
          result: openedModal.dialogRef.closed,
          close: () => close(modalElement),
          forceClose: () => {
            forceClose(modalElement);
            openedModal.dialogRef.close();
          },
        };
      }

      forceClose(modalElement);
      this.openedModals = this.openedModals.filter(
        (modal) => modal !== openedModal,
      );
    }

    const dialogRef = this.dialog.open<Result, Data, any>(component, {
      data,
      providers: config?.providers,
      hasBackdrop: false,
      disableClose: true,
    });
    const overlayContainerElement = document.querySelector(
      '.cdk-overlay-container',
    )!;
    const modalElement = getModalElement(dialogRef);

    if (config?.disableClose !== true) {
      const escapeKey$ = dialogRef.keydownEvents.pipe(
        filter((event) => event.key === 'Escape'),
        filter((event) => !event.defaultPrevented),
      );

      const mouseDown$ = fromEvent<MouseEvent>(document, 'mousedown');
      const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup');

      const backdropClick$ = zip(mouseDown$, mouseUp$).pipe(
        map(([down, up]) => [down.target, up.target]),
        filter((targets): targets is Node[] =>
          targets.every((target) => target instanceof Node),
        ),
        filter((targets) =>
          targets.every(
            (target) =>
              !overlayContainerElement.contains(target) ||
              target.parentElement?.tagName === this.ONYX_MODAL_TAG_NAME,
          ),
        ),
      );

      merge(escapeKey$, backdropClick$)
        .pipe(takeUntil(dialogRef.closed))
        .subscribe(() => close(modalElement));
    }

    if (modalElement?.getAttribute('variant') === 'right') {
      dialogRef.config.restoreFocus = false;
      dialogRef.overlayRef.updateScrollStrategy(new NoopScrollStrategy());
    }

    const modal = { component, data, dialogRef };
    this.openedModals.push(modal);

    dialogRef.closed.subscribe(() => {
      this.openedModals = this.openedModals.filter(
        (openedModal) => openedModal !== modal,
      );
    });

    return {
      close: () => close(modalElement),
      forceClose: () => {
        forceClose(modalElement);
        dialogRef.close();
      },
      result: dialogRef.closed,
    };
  }
}
